import React from 'react';
import PropTypes from 'prop-types';
import Tile from 'components/TileLegacy';
import { Tile as TileV2, LIVE_TILE, SEE_ALL_TILE } from 'components/TileV2';
import { isUpcomingContent } from 'helpers/ContentHelper';
import { noop } from 'helpers/GlobalHelper';

const CarouselItem = React.memo(
  ({
    applyInlineBackground,
    breakpoint,
    collectionType,
    disableHoverStateActionButtons,
    displayTemplate,
    favoritesManager,
    headline,
    isHoverStateV2,
    isLiveTileV2,
    isSeasons,
    isSeriesDetailAvailable,
    isSeriesDetailPage,
    item,
    itemIndex,
    onTileClick,
    onTileMouseDown,
    panelCount,
    playlist,
    preventLazyLoad,
    processed,
    renderImage,
    scaleFromLeft,
    scaleFromRight,
    searchCompleted,
    searchTerm,
    segmentPlaylistButtonAction,
    setSearchCompleted,
    shouldDisplayNetworkLogo,
    shouldInvertTitleOrder,
    subtitleLines,
    tileTheme,
    tileType,
    truncateSubtitle,
    upcomingOverlay,
  }) => {
    const callSignMap = window?.ExternalConfig?.callSignRedirects || {};
    const { seriesRedirectMap } = window?.ExternalConfig || {};
    let linkOverride = null;
    if (!item || item.length === 0 || !processed) return null;

    const upcomingOverlayClick = isUpcomingContent(item) ? upcomingOverlay : null;
    const { id, livePlayerScreenUrl } = processed || {};
    const key = `${id}-${itemIndex}`;
    const callSign = item?.callSign;
    const deepLinkWeb = item?.deeplinkWeb;

    if (callSignMap[callSign] && livePlayerScreenUrl) {
      linkOverride = callSignMap[callSign];
    }

    if (item?.seriesType === 'series' && seriesRedirectMap?.[item?.showCode]) {
      linkOverride = seriesRedirectMap?.[item?.showCode];
    }

    if (deepLinkWeb) {
      linkOverride = deepLinkWeb;
    }

    const tileProps = {
      ...processed,
      link: linkOverride || processed?.link,
      subtitleLink: linkOverride || processed?.subtitleLink,
      titleLink: linkOverride || processed?.titleLink,
      applyInlineBackground,
      breakpoint,
      callSign,
      collectionType,
      customTileClick: upcomingOverlayClick,
      'data-test': 'carousel-tile',
      disableHoverStateActionButtons,
      displayTemplate,
      favoritesManager,
      headline,
      isSeasons,
      isSeriesDetailAvailable,
      isSeriesDetailPage,
      itemIndex,
      key,
      preventLazyLoad,
      panelCount,
      playlist,
      onTileClick,
      onTileMouseDown,
      renderImage,
      scaleFromLeft,
      scaleFromRight,
      searchCompleted,
      setSearchCompleted,
      searchTerm,
      segmentPlaylistButtonAction,
      shouldDisplayNetworkLogo,
      shouldInvertTitleOrder,
      subtitleLines,
      theme: tileTheme,
      tileImageIndex: key,
      truncateSubtitle,
    };
    return isLiveTileV2 || tileType ? (
      <TileV2 {...tileProps} type={id === SEE_ALL_TILE ? SEE_ALL_TILE : tileType || LIVE_TILE} />
    ) : (
      <Tile {...tileProps} isHoverStateV2={isHoverStateV2} isLiveTileV2={isLiveTileV2} />
    );
  },
);

const CarouselItemWrapper = props => <CarouselItem {...props} />;

CarouselItem.defaultProps = {
  applyInlineBackground: false,
  breakpoint: '',
  collectionType: null,
  disableHoverStateActionButtons: false,
  favoritesManager: null,
  headline: {},
  isHoverStateV2: false,
  isLiveTileV2: false,
  isSeriesDetailPage: false,
  item: null,
  itemIndex: 0,
  onTileClick: noop,
  onTileMouseDown: noop,
  panelCount: 0,
  playlist: '',
  preventLazyLoad: false,
  processed: {},
  renderImage: noop,
  scaleFromLeft: false,
  scaleFromRight: false,
  searchCompleted: false,
  searchTerm: '',
  segmentPlaylistButtonAction: noop,
  setSearchCompleted: noop,
  shouldDisplayNetworkLogo: true,
  shouldInvertTitleOrder: noop,
  slideIndex: null,
  tileTheme: {},
  tileType: null,
  upcomingOverlay: noop,
};

CarouselItem.propTypes = {
  applyInlineBackground: PropTypes.bool,
  breakpoint: PropTypes.string,
  collectionType: PropTypes.string,
  disableHoverStateActionButtons: PropTypes.bool,
  displayTemplate: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  headline: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  isHoverStateV2: PropTypes.bool,
  isLiveTileV2: PropTypes.bool,
  isSeasons: PropTypes.bool,
  isSeriesDetailAvailable: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  item: PropTypes.shape(),
  itemIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onTileClick: PropTypes.func,
  onTileMouseDown: PropTypes.func,
  panelCount: PropTypes.number,
  playlist: PropTypes.string,
  preventLazyLoad: PropTypes.bool,
  processed: PropTypes.shape({}),
  renderImage: PropTypes.func,
  scaleFromLeft: PropTypes.bool,
  scaleFromRight: PropTypes.bool,
  searchCompleted: PropTypes.bool,
  searchTerm: PropTypes.string,
  segmentPlaylistButtonAction: PropTypes.func,
  setSearchCompleted: PropTypes.func,
  shouldDisplayNetworkLogo: PropTypes.bool,
  shouldInvertTitleOrder: PropTypes.func,
  slideIndex: PropTypes.number,
  subtitleLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tileTheme: PropTypes.shape(),
  tileType: PropTypes.string,
  truncateSubtitle: PropTypes.bool,
  upcomingOverlay: PropTypes.func,
};

export default React.memo(CarouselItemWrapper);
